import { useAtom, useAtomValue } from 'jotai'
import { useEffect } from 'react'
import chatBubbleGreen from '../../../assets/images/icons/chat_bubble_green.svg'
import chatBubbleRed from '../../../assets/images/icons/chat_bubble_red.svg'
import userMinusRed from '../../../assets/images/icons/user_minus_red.svg'
import { useModal } from '../../../hooks'
import { chatLocalMutesAtom, userInfoAtom } from '../../../store/atoms'
import { EModalName } from '../../../types/enums'
import { trpc } from '../../../utils'
import { Avatar, Icon, Scrollable } from '../../atoms'
import {
  actionButtons,
  avatarWrapper,
  button,
  drawerContainer,
  playersList,
  userBlockVariants,
  userInfoBlock,
} from './index.css'

export const TableMenuPlayers = (props: {
  tableId: string
  showAdminSettings: boolean
}) => {
  const {
    data: userRestrictions,
    refetch: refetchRestrictions,
    isLoading: isLoadingUserRestrictions,
  } = trpc.table.getUserRestrictions.useQuery({
    tableId: props.tableId,
  })
  const { mutateAsync: applyRestriction } =
    trpc.table.applyPlayerRestriction.useMutation()
  const { openConfirmationModal, openInfoModal, closeModal } = useModal()
  const userInfo = useAtomValue(userInfoAtom)!
  const [chatLocalMutes, setChatLocalMutes] = useAtom(chatLocalMutesAtom)

  const handleLocalMute = (userId: string, state: boolean) => {
    const updatedMutes = new Set(chatLocalMutes)

    if (state) {
      updatedMutes.add(userId)
    } else {
      updatedMutes.delete(userId)
    }

    setChatLocalMutes(updatedMutes)
  }

  const onClickMute = async (userId: string, state: boolean) => {
    handleLocalMute(userId, state)
    if (!props.showAdminSettings) {
      return
    }

    await applyRestriction({ userId, tableId: props.tableId, mute: state })
    await refetchRestrictions()
  }

  const onClickKick = async (userId: string, name: string) => {
    const { closeModal } = openConfirmationModal(
      {
        text: `Are you sure you want to kick player «${name}»?`,
        mainButtonText: 'Kick',
      },
      {
        title: 'Kick player',
        name: EModalName.CONFIRM_PLAYER_KICK,
      },
    )

    const result = await closeModal
    if (result !== 'main') {
      return
    }

    const response = await applyRestriction({
      userId,
      tableId: props.tableId,
      kick: true,
    })
    if (response?.status === 'success') {
      openInfoModal(
        {
          text: `Player «${name}» will be kicked at the end of the hand. Do not forget to change a password for the table`,
        },
        {
          title: 'Success',
          name: EModalName.INFO_PLAYER_KICKED,
        },
      )
      await refetchRestrictions()
    }
  }

  useEffect(() => {
    return () => {
      for (const modalName of [EModalName.CONFIRM_PLAYER_KICK]) {
        closeModal({
          name: modalName,
        })
      }
    }
  }, [])

  if (isLoadingUserRestrictions) {
    return null
  }

  if (!userRestrictions) {
    return (
      <div className={drawerContainer}>
        There are no players and no restrictions.
      </div>
    )
  }

  return (
    <div className={drawerContainer}>
      <Scrollable className={playersList}>
        {userRestrictions.map((user) => {
          if (!user || userInfo.id === user.id) {
            return null
          }

          const isLocallyMuted = chatLocalMutes.has(user.id)
          const isMuted = props.showAdminSettings ? user.mute : isLocallyMuted
          const muteIcon = isMuted ? chatBubbleGreen : chatBubbleRed
          const canKick =
            props.showAdminSettings &&
            user.isPlayer &&
            (!user.bannedUntil || new Date(user.bannedUntil) < new Date())

          return (
            <div
              key={user.id}
              className={
                userBlockVariants[user.isPlayer ? 'isPlayer' : 'isntPlayer']
              }
            >
              <div className={userInfoBlock}>
                <div className={avatarWrapper}>
                  <Avatar
                    avatar={user.avatar}
                    faceDirection='right'
                  />
                </div>
                <span>{user.name}</span>
              </div>
              <div className={actionButtons}>
                <Icon
                  src={muteIcon}
                  className={button}
                  analytic={{
                    name: 'mute',
                    params: {
                      modal: EModalName.TABLE_PLAYERS,
                      mute: !isMuted,
                    },
                  }}
                  onClick={() => {
                    void onClickMute(user.id, !isMuted)
                  }}
                />
                {canKick && (
                  <Icon
                    src={userMinusRed}
                    className={button}
                    analytic={{
                      name: 'kick',
                      params: {
                        modal: EModalName.TABLE_PLAYERS,
                        kick: true,
                      },
                    }}
                    onClick={() => {
                      void onClickKick(user.id, user.name)
                    }}
                  />
                )}
              </div>
            </div>
          )
        })}
      </Scrollable>
    </div>
  )
}
